import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  addMonths,
  addYears,
  format,
  lastDayOfMonth,
  subMonths,
  subYears,
} from "date-fns";
import Calendar from "./Calendar/Calendar";
import MonthView from "./MonthView/MonthView";
import { IconButton } from "../IconButton/IconButton";
import { DatePickerStyles } from "../../types";
import { ChevronLeftSolid, ChevronRightSolid } from "../../assets/icons";
import YearView from "./YearView/YearView";

const Container = styled.div<{
  styles: DatePickerStyles;
}>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  background-color: ${({ styles }) => styles.CalendarBackgroundColour};
  border: 2px solid ${({ styles }) => styles.CalendarBorderColour};
  border-radius: ${({ styles }) => styles.CalendarBorderRadius}px;
`;

const CurrentViewSelector = styled.div<{
  styles: DatePickerStyles;
}>`
  display: flex;
  justify-content: space-between;
  color: ${({ styles }) => styles.CalendarMonthDayTextColour};
`;

const CurrentViewButton = styled.button`
  align-self: center;
  font-weight: 700;
  font-size: inherit;
  font-family: inherit;
  cursor: pointer;
  color: inherit;
  background: none;
  border: none;
`;

const StyledIconButton = styled(IconButton)`
  color: inherit;
`;

export type DatePickerProps = {
  selectedDate: Date;
  setSelectedDate: React.Dispatch<React.SetStateAction<Date>>;
  styles: DatePickerStyles;
};

export const DatePicker: React.FC<DatePickerProps> = ({
  selectedDate,
  setSelectedDate,
  styles,
}) => {
  const { t } = useTranslation();
  const [currentView, setCurrentView] = useState<"day" | "month" | "year">(
    "day"
  );
  const [currentlyShownMonth, setCurrentlyShownMonth] =
    useState<Date>(selectedDate);

  const showPrevious = () => {
    const viewActions = {
      day: () => setCurrentlyShownMonth(subMonths(currentlyShownMonth, 1)),
      month: () => setCurrentlyShownMonth(subYears(currentlyShownMonth, 1)),
      year: () => setCurrentlyShownMonth(subYears(currentlyShownMonth, 12)),
    };

    viewActions[currentView]?.();
  };

  const showNext = () => {
    const viewActions = {
      day: () => setCurrentlyShownMonth(addMonths(currentlyShownMonth, 1)),
      month: () => setCurrentlyShownMonth(addYears(currentlyShownMonth, 1)),
      year: () => setCurrentlyShownMonth(addYears(currentlyShownMonth, 12)),
    };

    viewActions[currentView]?.();
  };

  const isLeftArrowDisabled = () => {
    const today = new Date();
    const viewActions = {
      day: () => today > lastDayOfMonth(subMonths(currentlyShownMonth, 1)),
      month: () =>
        today.getFullYear() > subYears(currentlyShownMonth, 1).getFullYear(),
      year: () => today.getFullYear() >= currentlyShownMonth.getFullYear(),
    };

    return viewActions[currentView]?.() ?? false;
  };

  const changeView = () => {
    if (currentView === "day") setCurrentView("month");
    if (currentView === "month") setCurrentView("year");
  };

  const getCurrentViewLabel = () => {
    const viewLabels = {
      day: `${t(format(currentlyShownMonth, "MMMM"))} ${format(currentlyShownMonth, "yyyy")}`,
      month: format(currentlyShownMonth, "yyyy"),
      year: `${currentlyShownMonth.getFullYear()} - ${currentlyShownMonth.getFullYear() + 11}`,
    };

    return viewLabels[currentView] ?? "";
  };

  return (
    <Container styles={styles} data-id="date-picker">
      <CurrentViewSelector styles={styles} data-id="current-view-selector">
        <StyledIconButton
          icon={ChevronLeftSolid}
          onClick={showPrevious}
          ariaLabel={t("Previous")}
          disabled={isLeftArrowDisabled()}
        />
        <CurrentViewButton
          type="button"
          onClick={changeView}
          data-id="current-view-button"
        >
          {getCurrentViewLabel()}
        </CurrentViewButton>
        <StyledIconButton
          icon={ChevronRightSolid}
          onClick={showNext}
          ariaLabel={t("Next")}
        />
      </CurrentViewSelector>
      {currentView === "day" && (
        <Calendar
          month={currentlyShownMonth}
          setMonth={setCurrentlyShownMonth}
          selectedDate={selectedDate ?? new Date()}
          setSelectedDate={setSelectedDate}
          styles={styles}
        />
      )}
      {currentView === "month" && (
        <MonthView
          setCurrentlyShownMonth={(date) => {
            setCurrentlyShownMonth(date);
            setCurrentView("day");
          }}
          currentlyShownYear={currentlyShownMonth}
        />
      )}
      {currentView === "year" && (
        <YearView
          setCurrentlyShownYear={(date) => {
            setCurrentlyShownMonth(date);
            setCurrentView("month");
          }}
          firstCurrentlyShownYear={currentlyShownMonth}
        />
      )}
    </Container>
  );
};

export default DatePicker;
